// BKLS Marketplace — pure, client-safe discovery logic.
//
// Nothing here invents catalog data. Every facet, score, and section is
// derived from fields that already exist on a published beat record
// (genre, mood, key, BPM, tags, collection, timestamps) or from measured
// analytics passed in by the caller. A beat that is not publicly published
// never reaches this module.

import { getTopic, type Topic } from "@/lib/taxonomy";
import type { LicenseTier, PublicBeat } from "@/lib/bkls";
import { tierAvailableFor, tierPriceFor } from "@/lib/bkls";

export interface MarketplaceBeat extends PublicBeat {
  collectionSlug: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
  /** Controlled topic slugs detected in the beat's own tags and copy. */
  topics: string[];
}

/** Measured engagement for a beat over the trending window. */
export interface BeatEngagement {
  slug: string;
  views: number;
  previews: number;
  licenseClicks: number;
}

export const TRENDING_WINDOW_DAYS = 30;

// ---------------- Facets ----------------

export interface BpmBand {
  key: string;
  label: string;
  min: number;
  max: number;
}

export const BPM_BANDS: BpmBand[] = [
  { key: "60-79", label: "60–79 BPM", min: 60, max: 79 },
  { key: "80-89", label: "80–89 BPM", min: 80, max: 89 },
  { key: "90-99", label: "90–99 BPM", min: 90, max: 99 },
  { key: "100-119", label: "100–119 BPM", min: 100, max: 119 },
  { key: "120-139", label: "120–139 BPM", min: 120, max: 139 },
  { key: "140+", label: "140+ BPM", min: 140, max: 400 },
];

export interface Facets {
  genres: string[];
  moods: string[];
  keys: string[];
  collections: string[];
  bpmBands: BpmBand[];
  topics: string[];
  tags: string[];
}

function uniqueSorted(values: (string | null | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v && v.trim())))).sort((a, b) =>
    a.localeCompare(b),
  );
}

/** Facets are computed from the visible result set — never hardcoded. */
export function facetsFor(beats: MarketplaceBeat[]): Facets {
  const tagCounts = new Map<string, number>();
  for (const b of beats) for (const t of b.tags) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
  return {
    genres: uniqueSorted(beats.map((b) => b.genre)),
    moods: uniqueSorted(beats.map((b) => b.mood)),
    keys: uniqueSorted(beats.map((b) => b.musical_key)),
    collections: uniqueSorted(beats.map((b) => b.collectionSlug)),
    bpmBands: BPM_BANDS.filter((band) =>
      beats.some((b) => typeof b.bpm === "number" && b.bpm >= band.min && b.bpm <= band.max),
    ),
    topics: uniqueSorted(beats.flatMap((b) => b.topics)),
    tags: Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 24)
      .map(([t]) => t),
  };
}

// ---------------- Filtering ----------------

export interface BeatFilters {
  q: string;
  genre: string;
  mood: string;
  musicalKey: string;
  collection: string;
  bpmBand: string;
  topic: string;
  tag: string;
  /** Only beats with at least one available fixed-price tier. */
  instantOnly: boolean;
}

export const EMPTY_FILTERS: BeatFilters = {
  q: "",
  genre: "",
  mood: "",
  musicalKey: "",
  collection: "",
  bpmBand: "",
  topic: "",
  tag: "",
  instantOnly: false,
};

export function activeFilterCount(filters: BeatFilters): number {
  let n = 0;
  if (filters.q.trim()) n++;
  for (const k of ["genre", "mood", "musicalKey", "collection", "bpmBand", "topic", "tag"] as const) {
    if (filters[k]) n++;
  }
  if (filters.instantOnly) n++;
  return n;
}

function matchesQuery(beat: MarketplaceBeat, needle: string): boolean {
  if (!needle) return true;
  const haystack = [
    beat.title,
    beat.subtitle ?? "",
    beat.description ?? "",
    beat.genre ?? "",
    beat.mood ?? "",
    beat.musical_key ?? "",
    beat.bpm ? `${beat.bpm} bpm` : "",
    beat.collectionSlug ?? "",
    beat.tags.join(" "),
  ]
    .join(" ")
    .toLowerCase();
  // Every whitespace-separated term must appear — narrows rather than widens.
  return needle
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

export function startingPriceCents(beat: MarketplaceBeat, tiers: LicenseTier[]): number | null {
  const prices = tiers
    .filter((t) => !t.is_quote_only && tierAvailableFor(t, beat))
    .map((t) => tierPriceFor(t, beat))
    .filter((p): p is number => typeof p === "number" && p > 0);
  return prices.length ? Math.min(...prices) : null;
}

export function applyBeatFilters(
  beats: MarketplaceBeat[],
  filters: BeatFilters,
  tiers: LicenseTier[] = [],
): MarketplaceBeat[] {
  const band = BPM_BANDS.find((b) => b.key === filters.bpmBand);
  const needle = filters.q.trim();
  return beats.filter((b) => {
    if (!matchesQuery(b, needle)) return false;
    if (filters.genre && b.genre !== filters.genre) return false;
    if (filters.mood && b.mood !== filters.mood) return false;
    if (filters.musicalKey && b.musical_key !== filters.musicalKey) return false;
    if (filters.collection && b.collectionSlug !== filters.collection) return false;
    if (filters.topic && !b.topics.includes(filters.topic)) return false;
    if (filters.tag && !b.tags.includes(filters.tag)) return false;
    if (band && !(typeof b.bpm === "number" && b.bpm >= band.min && b.bpm <= band.max)) return false;
    if (filters.instantOnly && startingPriceCents(b, tiers) == null) return false;
    return true;
  });
}

// ---------------- Sorting ----------------

export const SORT_OPTIONS = [
  { key: "newest", label: "Newest" },
  { key: "trending", label: "Trending" },
  { key: "price-asc", label: "Price: low to high" },
  { key: "price-desc", label: "Price: high to low" },
  { key: "bpm-asc", label: "Tempo: slow to fast" },
  { key: "bpm-desc", label: "Tempo: fast to slow" },
  { key: "title", label: "A–Z" },
] as const;

export type SortKey = (typeof SORT_OPTIONS)[number]["key"];

function time(value: string | null | undefined): number {
  return value ? new Date(value).getTime() : 0;
}

export function sortBeats(
  beats: MarketplaceBeat[],
  sort: SortKey,
  tiers: LicenseTier[] = [],
  engagement: BeatEngagement[] = [],
): MarketplaceBeat[] {
  const scores = new Map(engagement.map((e) => [e.slug, trendingScore(e)]));
  const price = (b: MarketplaceBeat) => startingPriceCents(b, tiers);
  const rows = [...beats];
  rows.sort((a, b) => {
    switch (sort) {
      case "trending":
        return (scores.get(b.slug) ?? 0) - (scores.get(a.slug) ?? 0) || time(b.publishedAt) - time(a.publishedAt);
      case "price-asc":
        return (price(a) ?? Number.MAX_SAFE_INTEGER) - (price(b) ?? Number.MAX_SAFE_INTEGER);
      case "price-desc":
        return (price(b) ?? -1) - (price(a) ?? -1);
      case "bpm-asc":
        return (a.bpm ?? Number.MAX_SAFE_INTEGER) - (b.bpm ?? Number.MAX_SAFE_INTEGER);
      case "bpm-desc":
        return (b.bpm ?? -1) - (a.bpm ?? -1);
      case "title":
        return a.title.localeCompare(b.title);
      case "newest":
      default:
        return time(b.publishedAt) - time(a.publishedAt) || a.title.localeCompare(b.title);
    }
  });
  return rows;
}

/**
 * Trending is measured, never editorial: a preview play is worth more than a
 * view, and a licence click more than either, because each is a deeper signal.
 */
export function trendingScore(e: BeatEngagement): number {
  return e.views + e.previews * 3 + e.licenseClicks * 6;
}

/** Beats with real measured engagement in the window, strongest first. */
export function trendingBeats(
  beats: MarketplaceBeat[],
  engagement: BeatEngagement[],
  limit = 6,
): MarketplaceBeat[] {
  const scored = engagement
    .map((e) => ({ e, beat: beats.find((b) => b.slug === e.slug) }))
    .filter((x): x is { e: BeatEngagement; beat: MarketplaceBeat } => Boolean(x.beat) && trendingScore(x.e) > 0)
    .sort((a, b) => trendingScore(b.e) - trendingScore(a.e));
  return scored.slice(0, limit).map((x) => x.beat);
}

// ---------------- Relationships ----------------

export interface RelatedBeat {
  beat: MarketplaceBeat;
  score: number;
  /** Human-readable, factual reasons — each one is a shared catalog attribute. */
  reasons: string[];
}

/**
 * Related beats are scored purely on shared, first-party attributes.
 * A beat with nothing in common scores 0 and is never surfaced, so an empty
 * list is a valid and honest result.
 */
export function relatedBeats(target: MarketplaceBeat, all: MarketplaceBeat[], limit = 4): RelatedBeat[] {
  const out: RelatedBeat[] = [];
  for (const beat of all) {
    if (beat.slug === target.slug) continue;
    let score = 0;
    const reasons: string[] = [];

    if (target.collectionSlug && beat.collectionSlug === target.collectionSlug) {
      score += 4;
      reasons.push("Same collection");
    }
    if (target.genre && beat.genre === target.genre) {
      score += 3;
      reasons.push(target.genre);
    }
    if (target.mood && beat.mood === target.mood) {
      score += 2;
      reasons.push(target.mood);
    }
    const sharedTopics = beat.topics.filter((t) => target.topics.includes(t));
    if (sharedTopics.length) {
      score += sharedTopics.length * 2;
      reasons.push(...sharedTopics);
    }
    const sharedTags = beat.tags.filter((t) => target.tags.includes(t));
    if (sharedTags.length) score += Math.min(sharedTags.length, 4);
    if (typeof target.bpm === "number" && typeof beat.bpm === "number" && Math.abs(target.bpm - beat.bpm) <= 8) {
      score += 2;
      reasons.push(`${beat.bpm} BPM`);
    }
    if (target.musical_key && beat.musical_key === target.musical_key) {
      score += 2;
      reasons.push(`Key of ${beat.musical_key}`);
    }

    if (score > 0) out.push({ beat, score, reasons: Array.from(new Set(reasons)).slice(0, 3) });
  }
  return out.sort((a, b) => b.score - a.score || a.beat.title.localeCompare(b.beat.title)).slice(0, limit);
}

// ---------------- Homepage sections ----------------

export interface MarketplaceSection {
  key: string;
  title: string;
  description: string;
  beats: MarketplaceBeat[];
  /** Where "view all" should lead, expressed as catalog filter state. */
  filter?: Partial<BeatFilters>;
}

/**
 * Sections are built from what exists. A section with no beats is dropped
 * entirely rather than rendered as an empty shelf.
 */
export function buildSections(
  beats: MarketplaceBeat[],
  engagement: BeatEngagement[],
  opts: { perSection?: number } = {},
): MarketplaceSection[] {
  const perSection = opts.perSection ?? 6;
  const sections: MarketplaceSection[] = [];

  const newest = sortBeats(beats, "newest").slice(0, perSection);
  if (newest.length) {
    sections.push({
      key: "newest",
      title: "Latest releases",
      description: "The most recently published productions in the licensing catalog.",
      beats: newest,
      filter: {},
    });
  }

  const trending = trendingBeats(beats, engagement, perSection);
  if (trending.length >= 2) {
    sections.push({
      key: "trending",
      title: "Moving right now",
      description: `Ranked by measured previews and licence interest over the last ${TRENDING_WINDOW_DAYS} days.`,
      beats: trending,
    });
  }

  // One shelf per genre that actually has more than one beat.
  const byGenre = new Map<string, MarketplaceBeat[]>();
  for (const b of beats) {
    if (!b.genre) continue;
    byGenre.set(b.genre, [...(byGenre.get(b.genre) ?? []), b]);
  }
  for (const [genre, rows] of Array.from(byGenre.entries()).sort((a, b) => b[1].length - a[1].length)) {
    if (rows.length < 2) continue;
    sections.push({
      key: `genre:${genre}`,
      title: genre,
      description: `Every ${genre.toLowerCase()} production currently open for licensing.`,
      beats: sortBeats(rows, "newest").slice(0, perSection),
      filter: { genre },
    });
  }

  return sections;
}

// ---------------- Formatting ----------------

export function formatDuration(seconds: number | null | undefined): string | null {
  if (typeof seconds !== "number" || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function beatMetaLine(beat: MarketplaceBeat): string {
  return [beat.genre, beat.mood, beat.bpm ? `${beat.bpm} BPM` : null, beat.musical_key, formatDuration(beat.duration_seconds)]
    .filter(Boolean)
    .join(" · ");
}

/** Resolve stored topic slugs back to their canonical definitions. */
export function topicLabels(slugs: string[]): Topic[] {
  return slugs.map((s) => getTopic(s)).filter((t): t is Topic => Boolean(t));
}
